import Error404 from 'pages/Error/Error404';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchCampaignApplications } from 'slices/campaigns/applications/thunk';
import { fetchCampaign } from 'slices/campaigns/thunk';
import { AppDispatch, RootState } from 'store';
import { Nav, NavLink, NavItem } from 'reactstrap';
import InfluencerPostModal from 'Components/influencer/InfluencerPostModal';
import CampaignPaymentsOffcanvas from 'Components/campaigns/CampaignPaymentsOffcanvas';
import { setHeaderCampaignData, setLastActiveCampaignTab, toggleApplicationOffcanvas, toggleApplicationView, toggleApproveApplicationCostModal, togglePostPerformanceOffcanvas } from 'slices/ui/reducer';
import PostPerformanceOffcanvas from 'Components/campaigns/collaborations/PostPerformanceOffcanvas';
import AccessControl from 'wrappers/AccessControl';
import { PRIVILEGE } from 'constants/privilege';
import { encodeBase64, generateUniqueKey } from 'utils/helpers';
import { defaultApplicationFilters, defaultShortlistedFilters } from 'Components/campaigns/applications/ApplicationFilters';
import { defaultCollaborationFilters } from 'pages/Campaigns/Collaborations';
import ApproveApplicationCost from 'Components/campaigns/applications/ApproveApplicationCost';
import Loader from 'Components/Common/Loader';
import ApplicationView from 'Components/campaigns/applications/ApplicationView';

interface CampaignLayoutProps {
    children: any,
    filters?: React.ReactNode,
    filtersToolbar?: React.ReactNode
}

const CampaignLayout: React.FC<CampaignLayoutProps> = ({ filters, filtersToolbar, children }) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { campaignId } = useParams();

    const loading = useSelector((state: RootState) => state.campaigns.loading);

    const campaignDetail = useSelector((state: RootState) => state.campaigns.items[campaignId!]);
    const campaignData = campaignDetail?.data ?? null;
    const configurations = useSelector((state: RootState) => state.auth.configurations);
    const creatorRecommendationInitiated = campaignData?.campaign?.creator_recommendation_initiated ?? false;
    const creatorRecommendationAvailable = campaignData?.campaign?.creator_recommendation_available ?? false;
    const showRecommendedCreatorsTab = creatorRecommendationInitiated && creatorRecommendationAvailable;

    const campaignApplicationsState = useSelector(
        (state: RootState) => state.campaignApplications.campaigns[campaignId!]
    );
    const applicationFilters = useSelector((state: RootState) => state.campaignApplications.filtersApplications);
    const shortlistedFilters = useSelector((state: RootState) => state.campaignApplications.filtersShortlisted);

    const applicationsFilterId = generateUniqueKey(
        { ...(applicationFilters ?? defaultApplicationFilters), bookmarked: 'n' },
        ['page']
    );
    const defaultShortlistedFilterId = useMemo(
        () => generateUniqueKey({ ...(shortlistedFilters ?? defaultShortlistedFilters), bookmarked: 'y' }, ['page']),
        [shortlistedFilters]
    );

    const filteredApplicationsCount =
        campaignApplicationsState?.filtered_lists?.[applicationsFilterId]?.count ?? 0;
    const shortlistedTabCount =
        campaignApplicationsState?.filtered_lists?.[defaultShortlistedFilterId]?.count ?? 0;
    const totalApplicationsCount =
        campaignApplicationsState?.total ?? 0;

    const shortlistTabPulse = useSelector((state: RootState) => state.ui.shortlistTabPulse);
    const [shortlistBadgePulse, setShortlistBadgePulse] = useState(false);


    useEffect(() => {
        if (shortlistTabPulse > 0) {
            setShortlistBadgePulse(true);
            const timer = setTimeout(() => setShortlistBadgePulse(false), 650);
            return () => clearTimeout(timer);
        }
    }, [shortlistTabPulse]);

    const totalCollaborationsCount = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.filtered_lists?.[state.collaborations.applied_filter_id!]?.count) ?? 0;
    const totalPostsCount = useSelector((state: RootState) => state.campaignPermalinks.campaigns[campaignId!]?.filtered_lists?.[state.campaignPermalinks.applied_filter_id!]?.count) ?? 0;

    const influencerPostModalVisibile = useSelector((state: RootState) => state.ui.influencerPostModal);

    const applicationOffcanvas = useSelector((state: RootState) => state.ui.applicationOffcanvas);
    const applicationView = useSelector((state: RootState) => state.ui.applicationView);
    const postPerformanceOffcanvas = useSelector((state: RootState) => state.ui.postPerformanceOffcanvas);
    const approveApplicationModal = useSelector((state: RootState) => state.ui.approveApplicationCostModal);

    const collaborationFilters = useSelector((state: RootState) => state.collaborations.filters);

    const collaborationsTabCount = totalCollaborationsCount > 0 ? totalCollaborationsCount : campaignData?.campaign?.collaborations_count ?? 0;


    useEffect(() => {
        if (campaignId && !campaignData) {
            dispatch(fetchCampaign({ campaignId }));
        }
    }, [campaignId, dispatch, campaignData]);

    // Tab badge: filtered count for default shortlisted filters (bookmarked=y), not unfiltered DB total.
    useEffect(() => {
        if (!campaignId || campaignData?.campaign?.mode === 'offline') return;

        dispatch(
            fetchCampaignApplications({
                campaignId,
                filters: { ...defaultShortlistedFilters, page: 1 },
                silent: true,
            })
        );
    }, [campaignId, campaignData?.campaign?.mode, dispatch]);

    useEffect(() => {
        if (campaignData) {
            dispatch(setHeaderCampaignData({ campaign: campaignData }));
        }

        return () => {
            if (campaignData) {
                dispatch(setHeaderCampaignData({ campaign: null, detailPage: false }));
            }
        }
    }, [campaignData])

    const getTabId = useMemo(() => {
        const path = location.pathname;
        if (path.includes('/shortlisted')) return 'shortlisted';
        if (path.includes('/applications')) return 'applications';
        if (path.includes('/collaborations')) return 'collaborations';
        if (path.includes('/insights')) return 'insights';
        if (path.includes('/add-posts')) return 'add-posts';
        if (path.includes('/links-status')) return 'links-status';
        if (path.includes('/recommended-creators')) return 'recommended-creators';
        return '';
    }, [location.pathname]);

    const getPageTitle = useMemo(() => {
        if (!campaignData) return '';

        switch (getTabId) {
            case 'applications':
                return 'Applications Received';
            case 'shortlisted':
                return 'Shortlisted';
            case 'collaborations':
                return 'Collaborations';
            case 'insights':
                return 'Campaign Insights';
            case 'recommended-creators':
                return 'Recommended Creators';
            default:
                return 'Campaign';
        }
    }, [campaignData, getTabId]);

    useEffect(() => {
        if (['applications', 'shortlisted', 'collaborations', 'insights', 'recommended-creators'].includes(getTabId)) {
            dispatch(
                setLastActiveCampaignTab(
                    getTabId as 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators'
                )
            );
        }
    }, [getTabId, dispatch])

    useEffect(() => {
        if (campaignData && campaignData?.campaign?.mode === 'offline' && getTabId === 'applications') {
            navigate(`/campaign/${campaignId}/insights`)
        } else if(campaignData && campaignData?.campaign?.mode !== 'offline' && getTabId === 'add-posts'){
            navigate(`/campaign/${campaignId}/applications`)
        }
    }, [campaignData, getTabId])

    const getRecentFiltersQuery = (dataSet: 'applications' | 'collaborations' | 'shortlisted') => {
        let filters: any = null;

        switch (dataSet) {
            case 'applications':
                filters = {
                    ...(applicationFilters ?? defaultApplicationFilters),
                    bookmarked: 'n',
                    page: 1,
                };
                break;
            case 'shortlisted':
                filters = {
                    ...(shortlistedFilters ?? defaultShortlistedFilters),
                    bookmarked: 'y',
                    page: 1,
                };
                break;
            case 'collaborations':
                filters = collaborationFilters ?? defaultCollaborationFilters;
                break;
            default:
                filters = null;
        }

        if (!filters) return '';
        return `?filters=${encodeBase64(filters)}`;
    }

    if (loading && !campaignData) return <Loader />;

    if ((!loading && !campaignData) || !campaignId) return <Error404 />;

    return (
        <div>
            <div className="campaign-page">
                <h1 className="campaign-page-title">{getPageTitle}</h1>
                <div className="d-flex justify-content-between align-items-center campaign-page-info flex-column flex-lg-row mt-4 gap-2 gap-lg-0">
                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav w-100 w-lg-75">
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link d-flex align-items-center ${getTabId === 'applications' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/applications${getRecentFiltersQuery('applications')}`)}
                                >
                                    Applications
                                    {
                                        totalApplicationsCount ? (
                                            <React.Fragment>
                                                <span className="campaign-nav-tab-badge text-white d-flex align-items-center gap-1" style={{borderRadius: '4px'}}>{filteredApplicationsCount} <span>of</span> {totalApplicationsCount}</span>
                                            </React.Fragment>
                                        ) : (
                                            <span className="campaign-nav-tab-badge text-white d-flex align-items-center gap-1">{campaignData?.campaign?.applications_count ?? 0}</span>
                                        )
                                    }
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'shortlisted' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/shortlisted${getRecentFiltersQuery('shortlisted')}`)}
                                >
                                    <span className="d-inline-flex align-items-center">
                                        Shortlisted
                                        {shortlistedTabCount > 0 && (
                                            <span
                                                className={`campaign-nav-tab-badge ${
                                                    shortlistBadgePulse ? 'campaign-nav-tab-badge--pulse' : ''
                                                }`}
                                            >
                                                {shortlistedTabCount}
                                            </span>
                                        )}
                                    </span>
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'collaborations' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/collaborations${getRecentFiltersQuery('collaborations')}`)}
                                >
                                    <span className="d-inline-flex align-items-center">
                                        Collaborations
                                        {collaborationsTabCount > 0 && (
                                            <span className="campaign-nav-tab-badge">
                                                {collaborationsTabCount}
                                            </span>
                                        )}
                                    </span>
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.PERFORMANCE.INDEX}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'insights' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/insights`)}
                                >
                                    Campaign Insights
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode === 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'add-posts' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/add-posts`)}
                                >
                                    Add Posts
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        {
                            showRecommendedCreatorsTab && (
                                <NavItem className='campaign-nav-item'>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={`campaign-nav-link ${getTabId === 'recommended-creators' && 'active-campaign-nav'} text-start`}
                                        onClick={() => navigate(`/campaign/${campaignId}/recommended-creators`)}
                                    >
                                        Recommended Creators
                                    </NavLink>
                                </NavItem>
                            )
                        }
                        {/* <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'links-status' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/links-status`)}
                                >
                                    Links Status
                                </NavLink>
                            </NavItem>
                        </AccessControl> */}
                    </Nav>
                    {filters}
                </div>
                {
                    Boolean(filtersToolbar) && (
                        <div className="campaign-page-filters">
                            {filtersToolbar}
                        </div>
                    )
                }
                <div className="campaign-page-content my-3">
                    {children}
                </div>
            </div>
            <InfluencerPostModal
                isOpen={!!influencerPostModalVisibile}
                onClose={() => { }}
                postId={influencerPostModalVisibile?.postId!}
                collaborationId={influencerPostModalVisibile?.collaborationId!}
                influencer={influencerPostModalVisibile?.influencer!}
                socialAccount={influencerPostModalVisibile?.socialAccount!}
                status={influencerPostModalVisibile?.status}
                preventAction={!!influencerPostModalVisibile?.preventAction}
            />
            {
                configurations?.agency_show_campaign_payments === 'y' && <CampaignPaymentsOffcanvas />
            }
            <ApplicationView
                isOpen={!!applicationView}
                applicationId={applicationView?.applicationId!}
                campaignId={applicationView?.campaignId!}   
                socialAccountId={applicationView?.socialAccountId!}
                toggleOffcanvas={() => dispatch(toggleApplicationView(null))}
                showPermalink={applicationView?.showPermalink ?? true}
            />
            <PostPerformanceOffcanvas
                isOpen={!!postPerformanceOffcanvas}
                collaborationId={postPerformanceOffcanvas?.collaborationId!}
                onClose={() => dispatch(togglePostPerformanceOffcanvas(null))}
            />
            <ApproveApplicationCost 
                application={approveApplicationModal?.application!}
                campaignId={campaignId!}
                isOpen={approveApplicationModal?.isOpen!}
                onClose={() => dispatch(toggleApproveApplicationCostModal(null))}
            />
        </div>
    );
};

export default CampaignLayout;